<template>
  <ControlWrapper
    v-if="!hasAllProperties"
    v-bind="control.controlWrapper.value"
    :styles="control.styles"
    :applied-options="control.appliedOptions"
    :error="control.control.value.errors"
  >
    <SectionWrapper :title="title" :description :title-class="control.styles.control.label">
      <div class="flex w-full flex-col gap-8">
        <form v-if="!hasAllUserProperties" class="flex w-full flex-col gap-4">
          <WInput
            v-if="hasRetailerName"
            v-model="user.retailerName"
            type="text"
            name="retailerName"
            autocomplete="given-name"
            subtitle
            :label="`${controlOptions.properties?.user.properties?.retailerName?.title || 'Retailer Name'}${isRequired('retailerName') ? '*' : ''}`"
            :error="showErrors && isRequired('retailerName') && !user.retailerName ? 'This field is required' : ''"
            @update:model-value="capitaliseName"
          >
          </WInput>
          <WInput
            v-model="user.firstName"
            type="text"
            name="firstName"
            autocomplete="given-name"
            subtitle
            :label="`${controlOptions.properties?.user.properties.firstName.title || 'First name'}${isRequired('firstName') ? '*' : ''}`"
            :error="showErrors && isRequired('firstName') && !user.firstName ? 'This field is required' : ''"
            @update:model-value="capitaliseName"
          >
          </WInput>

          <WInput
            v-model="user.lastName"
            type="text"
            name="lastName"
            autocomplete="family-name"
            :label="`${controlOptions.properties?.user.properties.lastName.title}${isRequired('lastName') ? '*' : ''}`"
            subtitle
            @update:model-value="capitaliseName"
            :error="showErrors && isRequired('lastName') && !user.lastName ? 'This field is required' : ''"
          />

          <WInput
            v-model="user.email"
            type="email"
            inputmode="email"
            name="email"
            autocmplete="email"
            :label="`${controlOptions.properties?.user.properties.email.title}${isRequired('email') ? '*' : ''}`"
            subtitle
            @update:model-value="validateEmailAndSetError"
            :error="emailError || (showErrors && !user.email && isRequired('email') ? 'This field is required' : '')"
          />

          <PhoneInput
            :phone="user.phone.formatted"
            subtitle
            :label="`${controlOptions.properties?.user.properties.phone.title}${isRequired('phone') ? '*' : ''}`"
            placeholder=""
            class="text-base"
            inner-class="vehicle-phone-input"
            @phone-changed="phoneChange"
            @error="handlePhoneError"
            :error-message="
              phoneError || (showErrors && !user.phone.formatted && isRequired('phone')) ? 'This field is required' : ''
            "
          />
        </form>
      </div>
    </SectionWrapper>
  </ControlWrapper>
</template>

<script setup lang="ts">
  import { nextTick, ref, watch, onBeforeUnmount, computed } from 'vue';
  import { ControlElement } from '@jsonforms/core';
  import { rendererProps, useJsonFormsControl } from '@jsonforms/vue';
  import { useWidgetControl } from '../util';
  import { capitalise, validateEmail } from 'src/utils/helpers';
  import { Phone } from 'src/types/overlays';
  import ControlWrapper from './ControlWrapper.vue';
  import SectionWrapper from '../../claims-widget/components/SectionWrapper.vue';
  import WInput from '../../base/Input.vue';
  import PhoneInput from '../../base/PhoneInput.vue';
  import { useWidgetError } from '../util/composition';
  import { PublicUserConfig } from 'src/types/widget';
  import type { ContactProperties } from '../../../types/widget';

  interface PropertyValue {
    title: string;
    type: string;
  }

  interface UserProperty {
    retailerName?: PropertyValue;
    firstName: PropertyValue;
    lastName: PropertyValue;
    email: PropertyValue;
    phone: PropertyValue;
  }

  interface Contact {
    required: Array<keyof UserProperty>;
    type: string;
    title: string;
    label: string;
    properties: UserProperty;
  }

  interface Property {
    user: Contact;
  }

  interface ContactPropertiesConfig {
    required: Array<keyof Property>;
    contact: true;
    type: 'object';
    title: string;
    properties: Property;
    description?: string;
  }

  const props = defineProps(rendererProps<ControlElement>());

  const { onValidate, errors } = useWidgetError();
  const control = useWidgetControl(useJsonFormsControl(props)); // Access control properties via control.xxx

  const controlData = computed<Partial<ContactProperties>>(() => control.control.value?.data);
  const controlOptions = computed(() => control.control.value?.schema as Partial<ContactPropertiesConfig>);
  const hasRetailerName = computed(() => controlOptions.value?.properties?.user?.properties?.retailerName?.title);
  const isUserRequired = computed(() => controlOptions.value?.required?.includes('user'));
  const title = computed(() => control.computedLabel.value || 'Contact details'); // Access computedLabel via control
  const description = computed(() => controlOptions.value?.description || '');

  const user = ref<Omit<PublicUserConfig, 'phone'> & { phone: Phone }>(
    controlData.value?.user
      ? {
          ...controlData.value.user,
          phone: getFormattedPhone(controlData.value.user.phone),
        }
      : {
          firstName: '',
          lastName: '',
          email: '',
          phone: {
            formatted: '',
          },
          ...(hasRetailerName.value ? { retailerName: '' } : {}),
        }
  );
  const hasAllUserProperties = ref(
    !!(user.value.firstName && user.value.lastName && user.value.email && user.value.phone?.formatted)
  );
  const hasAllProperties = ref(!!hasAllUserProperties.value);
  const emailError = ref('');
  const phoneError = ref('');
  const showErrors = ref(false);

  const listener = onValidate(() => {
    if (isUserRequired.value || emailError.value || phoneError.value) {
      showErrors.value = true;
    }
  });
  onBeforeUnmount(listener.off);

  function isRequired(key: keyof UserProperty) {
    return controlOptions.value.properties?.user?.required?.includes?.(key);
  }

  function capitaliseName() {
    user.value.firstName = capitalise(user.value.firstName || '');
    user.value.lastName = capitalise(user.value.lastName || '');
  }

  function validateEmailAndSetError(email?: string) {
    emailError.value = validateEmail(email) ? '' : 'Email must be a valid email address';
    errors.value['contact-email'] = emailError.value;
  }

  function handlePhoneError(hasError: boolean) {
    phoneError.value = hasError ? 'Invalid phone number' : '';
    errors.value['contact-phone'] = phoneError.value;
  }

  function phoneChange(phone: Phone) {
    user.value.phone = phone;
  }

  function getFormattedPhone(phone?: Phone | string): Phone {
    if (!phone) {
      return {
        formatted: '',
      };
    }
    if (typeof phone === 'string') {
      return {
        formatted: phone,
      };
    }
    return phone;
  }

  watch(
    user,
    () => {
      nextTick(() => {
        control.onChange({
          user: {
            firstName: user.value.firstName || undefined,
            lastName: user.value.lastName || undefined,
            email: user.value.email || undefined,
            phone: user.value.phone || undefined,
            ...(hasRetailerName.value ? { retailerName: user.value.retailerName || undefined } : {}),
          },
        });
      });
    },
    { immediate: true, deep: true }
  );
</script>
